import React, { useEffect, useState, useMemo } from 'react';
import {
    Modal as ModalContainer,
    Box,
    FormGroup,
    Grid,
    FormControlLabel,
    Typography
} from '@mui/material';
import styled from '@emotion/styled';
import {
    SecondaryButtonRegular,
    PrimaryButton
} from '@/components/shared/Button';
import { useTranslation } from 'react-i18next';
import { FONT_WEIGHT, TEXT_SIZE, Title } from '@/typography/Typography';
import { Checkbox } from '@/components/shared/CheckBox';
import { RadioButton } from '@/components/shared/RadioButton';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { UserRoles } from '@/models/company';
import { BlueCircle } from '@/icons/BlueCircle';
import { useErrorHandling } from '@/utils/errorHandling';
import { useTheme } from '@emotion/react';
import {
    useGetUserCompanies,
    useGetAdminCompanies
} from '@/services/paymentsQuery';
import { useUsersMutations } from '@/services/usersMutations';

const ConfirmationBox = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 1200px;
    max-height: 611px;
    min-width: 400px;
    background-color: #ffffff;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 5px 20px;
    outline: none;
    display: inline-table;
    justify-content: center;
`;

const AccessRightGrid = styled(Grid)`
    border: 1px solid #d0d5dd;
    padding: 0;
    max-height: 100%;
    max-width: 100%;
    border-radius: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ExtraRow = styled(Typography)`
    align-self: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

interface EditUsersProps {
    onClose?: () => void;
    dialogOpen: boolean;
    rowData: any;
    refreshList: any;
    reloadCompanyUsersService: () => void;
}

type CheckboxItem<T> = {
    id: string;
    name: T;
    isUsersSyncEnabled: boolean;
};

interface StateChange {
    id: string;
    newState: boolean;
}

export const EditUsersDialog = (props: EditUsersProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { createUserInMultiCompanies } = useUsersMutations();

    // User role selection state - Only ONE role can be selected (Admin or Manager)
    const [selectedRole, setSelectedRole] = useState<UserRoles>(
        props.rowData.role === UserRoles.Admin
            ? UserRoles.Admin
            : UserRoles.Manager
    );

    const [checkboxStateChanges, setCheckboxStateChanges] = useState<
        StateChange[]
    >([]);

    // Reset selected role when dialog opens with new user data
    useEffect(() => {
        setSelectedRole(
            props.rowData.role === UserRoles.Admin
                ? UserRoles.Admin
                : UserRoles.Manager
        );
    }, [props.rowData.role]);

    // Fetch user's companies with React Query
    const { data: userCompaniesData, isLoading: isLoadingUserCompanies } =
        useGetUserCompanies(
            props.rowData.userId,
            props.dialogOpen && !!props.rowData.userId
        );

    // Fetch all admin companies with React Query
    const { data: allCompaniesData, isLoading: isLoadingAllCompanies } =
        useGetAdminCompanies(props.dialogOpen);

    // Compute which companies should be checked based on selected role
    const adminCompaniesList = useMemo(() => {
        if (!allCompaniesData || !userCompaniesData) return [];

        // Get companies where user has the currently selected role
        const enabledCompanyIds = new Set(
            userCompaniesData
                .filter((company: any) => company.role === selectedRole)
                .map((company: any) => company.id)
        );

        return allCompaniesData.map((company: any) => ({
            ...company,
            isUsersSyncEnabled: enabledCompanyIds.has(company.id)
        }));
    }, [allCompaniesData, userCompaniesData, selectedRole]);

    const ModalHeading = ({ label }: { label: string }) => {
        return (
            <Typography
                fontWeight={FONT_WEIGHT.BOLD}
                fontSize={TEXT_SIZE.MEDIUM_SMALL}
                m={1}
            >
                {label}
            </Typography>
        );
    };

    const handleRoleChange = (role: UserRoles) => {
        setSelectedRole(role);
        // Clear checkbox state changes when role changes
        setCheckboxStateChanges([]);
    };

    const renderCheckboxes = (title: string, items: CheckboxItem<string>[]) => {
        return (
            <AccessRightGrid item xs={12}>
                <Title
                    fontWeight={600}
                    p={'10px 12px'}
                    textAlign={'start'}
                    borderBottom={'1px solid #EAECF0'}
                >
                    {title}
                </Title>
                <FormGroup
                    sx={{
                        overflowY: 'auto',
                        maxHeight: '230px',
                        display: 'block',
                        textAlign: 'start'
                    }}
                >
                    {items.map((item, index) => (
                        <FormControlLabel
                            sx={{
                                borderTop: '1px solid #D0D5DD',
                                margin: 0,
                                display: 'block',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis'
                            }}
                            onClick={(e) => {
                                e.preventDefault();

                                // Get current effective state (considering any pending changes)
                                const change = checkboxStateChanges.find(
                                    (c) => c.id === item.id
                                );
                                const currentState =
                                    change !== undefined
                                        ? change.newState
                                        : item.isUsersSyncEnabled;
                                const newState = !currentState;

                                const existingIndex =
                                    checkboxStateChanges.findIndex(
                                        (change) => change.id === item.id
                                    );

                                if (existingIndex === -1) {
                                    setCheckboxStateChanges((prev) => [
                                        ...prev,
                                        { id: item.id, newState }
                                    ]);
                                } else {
                                    setCheckboxStateChanges((prev) => {
                                        const updated = [...prev];
                                        updated[existingIndex] = {
                                            id: item.id,
                                            newState
                                        };
                                        return updated;
                                    });
                                }
                            }}
                            key={index}
                            control={
                                <Checkbox
                                    size="small"
                                    sx={{
                                        padding: '0.3rem 0.5rem',
                                        color: '#ECFDF3 !important',
                                        borderColor: '#089855 !important'
                                    }}
                                    checked={(() => {
                                        const change =
                                            checkboxStateChanges.find(
                                                (c) => c.id === item.id
                                            );
                                        return change !== undefined
                                            ? change.newState
                                            : item.isUsersSyncEnabled;
                                    })()}
                                />
                            }
                            label={item.name}
                        />
                    ))}
                </FormGroup>
            </AccessRightGrid>
        );
    };

    const renderRoleSelection = () => {
        const roles = [
            { value: UserRoles.Admin, label: UserRoles.Admin },
            { value: UserRoles.Manager, label: UserRoles.Manager }
        ];

        return (
            <Grid
                item
                xs={12}
                border={'1px solid #D0D5DD'}
                padding={0}
                height={'100%'}
                borderRadius={'2px'}
                maxHeight={'100%'}
            >
                <Title
                    fontWeight={600}
                    p={'10px 12px'}
                    textAlign={'start'}
                    borderBottom={'1px solid #EAECF0'}
                >
                    {t('user_type')}
                </Title>
                <FormGroup sx={{ overflow: 'hidden' }}>
                    {roles.map((role, index) => (
                        <FormControlLabel
                            sx={{
                                borderTop: '1px solid #D0D5DD',
                                margin: 0
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                handleRoleChange(role.value);
                            }}
                            key={index}
                            control={
                                <RadioButton
                                    size="small"
                                    checked={selectedRole === role.value}
                                    customRadio
                                    checkedColor={theme.palette.text.success}
                                />
                            }
                            label={role.label}
                        />
                    ))}
                </FormGroup>
            </Grid>
        );
    };

    const getInitials = (name: string) => {
        const hasTokens = name.indexOf(' ') !== -1;
        return (
            name.substring(0, hasTokens ? 1 : 2) +
            (hasTokens ? name.charAt(name.lastIndexOf(' ') + 1) : '')
        );
    };

    // Calculate if all companies are checked (considering state changes)
    const allCompaniesChecked = useMemo(() => {
        if (!adminCompaniesList.length) return false;

        return adminCompaniesList.every((company) => {
            const change = checkboxStateChanges.find(
                (c) => c.id === company.id
            );
            return change !== undefined
                ? change.newState
                : company.isUsersSyncEnabled;
        });
    }, [adminCompaniesList, checkboxStateChanges]);

    const handleSelectAllCompanies = () => {
        const newState = !allCompaniesChecked;

        const stateChanges: StateChange[] = adminCompaniesList.map(
            (company) => ({
                id: company.id,
                newState
            })
        );

        setCheckboxStateChanges(stateChanges);
    };

    const handleSubmit = () => {
        // Only include companies where the final state differs from the original
        const updatedCompanies = checkboxStateChanges
            .filter((change) => {
                const originalCompany = adminCompaniesList.find(
                    (company) => company.id === change.id
                );
                // Only include if the new state is different from original
                return (
                    originalCompany &&
                    change.newState !== originalCompany.isUsersSyncEnabled
                );
            })
            .map((change) => ({
                companyId: change.id,
                action: change.newState ? 'add' : 'delete'
            }));

        createUserInMultiCompanies.mutate(
            {
                firstName: props.rowData.firstName,
                lastName: props.rowData.lastName,
                email: props.rowData.email,
                phoneNumber: props.rowData.phoneNumber,
                isActive: props.rowData.isActive,
                role: selectedRole,
                companies: updatedCompanies
            },
            {
                onSuccess: () => {
                    props.refreshList();
                    props.reloadCompanyUsersService();
                    if (props.onClose) {
                        props.onClose();
                    }
                }
            }
        );
    };

    const isLoading =
        isLoadingUserCompanies ||
        isLoadingAllCompanies ||
        createUserInMultiCompanies.isPending;

    return (
        <ModalContainer open={props.dialogOpen} onClose={props.onClose}>
            <ConfirmationBox>
                <Box textAlign={'center'} marginTop={'10px'}>
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        alignItems={'center'}
                    >
                        <ModalHeading label={t('edit_users')} />
                        <BlueCircle />
                        {props.rowData && (
                            <Typography
                                fontSize={20}
                                fontWeight={'bold'}
                                marginTop={'-40px'}
                                marginBottom={2}
                                color={'#1570EF'}
                            >
                                {getInitials(
                                    props.rowData.firstName +
                                        ' ' +
                                        props.rowData?.lastName
                                )}
                            </Typography>
                        )}
                        <Typography
                            color={'#089855'}
                            fontWeight={FONT_WEIGHT.SEMI_BOLD}
                        >
                            {props.rowData.firstName +
                                ' ' +
                                props.rowData?.lastName}
                        </Typography>
                    </Box>

                    <Grid
                        container
                        justifyContent={'space-between'}
                        marginTop={'20px'}
                    >
                        <Grid item xs={5.8}>
                            {renderRoleSelection()}
                        </Grid>
                        <Grid item xs={5.8}>
                            {isLoading ? (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    p={2}
                                >
                                    <Typography>{t('loading')}...</Typography>
                                </Box>
                            ) : (
                                renderCheckboxes(
                                    t('give_access_rights'),
                                    adminCompaniesList
                                )
                            )}
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        justifyContent={'space-between'}
                        margin={'0px 0px 20px'}
                    >
                        <Grid item xs={5.8}></Grid>
                        <Grid
                            item
                            xs={5.8}
                            display={'flex'}
                            sx={{
                                backgroundColor: '#F2F4F7',
                                cursor: 'pointer'
                            }}
                            onClick={handleSelectAllCompanies}
                        >
                            <Checkbox
                                sx={{
                                    margin: '0px 0px 0px 1px',
                                    padding: '0.3rem 0.5rem'
                                }}
                                edge="start"
                                checked={allCompaniesChecked}
                                size="small"
                                tabIndex={-1}
                                inputProps={{
                                    style: {
                                        borderRadius: '4px'
                                    },
                                    'aria-labelledby':
                                        'checkbox-list-secondary-label-all'
                                }}
                            />
                            <ExtraRow>{t('select_all_companies')}</ExtraRow>
                        </Grid>
                    </Grid>

                    <Typography textAlign={'start'}>
                        {t('edit_users_note')}
                    </Typography>
                    <Grid
                        m={'20px 0'}
                        container
                        justifyContent={'space-around'}
                    >
                        <SecondaryButtonRegular
                            onClick={props.onClose}
                            sx={{ width: '48%' }}
                        >
                            {t('close')}
                        </SecondaryButtonRegular>

                        <PrimaryButton
                            sx={{ width: '48%' }}
                            onClick={handleSubmit}
                            disabled={isLoading}
                        >
                            {isLoading ? t('saving') : t('save_settings')}
                        </PrimaryButton>
                    </Grid>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
